import { debounce } from 'lodash';

export enum FileEventType {
    WRITE = 'WRITE',
    DELETE = 'DELETE',
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    RENAME = 'RENAME',
    MOVE = 'MOVE',
}

export type FileEvent = {
    type: FileEventType;
    path: string;
    timestamp: number;
}

class FileEventQueue {
    private queue: FileEvent[] = [];
    private isProcessing = false;
    private eventCallbacks: Map<string, (events: FileEvent[]) => Promise<void>> = new Map();
    private processDebounced: () => void;

    constructor(delayMs = 300) {
        this.processDebounced = debounce(this.processQueue.bind(this), delayMs);
    }

    public setEventCallback(key: string, callback: (events: FileEvent[]) => Promise<void>) {
        this.eventCallbacks.set(key, callback);
    }

    public removeEventCallback(key: string) {
        this.eventCallbacks.delete(key);
    }

    public addEvent(event: FileEvent) {
        // 检查是否已存在相同路径的相同类型事件，有则替换
        const existingIndex = this.queue.findIndex(
            e => e.path === event.path && e.type === event.type
        );

        if (existingIndex !== -1) {
            this.queue[existingIndex] = event; // 用新事件替换旧事件
        } else {
            this.queue.push(event);
        }

        this.processDebounced();
    }

    private async processQueue() {
        if (this.isProcessing || this.queue.length === 0 || this.eventCallbacks.size === 0) {
            return;
        }

        this.isProcessing = true;

        // 创建要处理的事件批次
        const events = [...this.queue];
        this.queue = [];

        // 合并和优化事件
        const optimizedEvents = this.optimizeEvents(events);

        try {
            // Call all registered callbacks with the events
            await Promise.all(
                Array.from(this.eventCallbacks.values()).map(callback => {
                    callback(optimizedEvents)
                })
            );
        } catch (error) {
            console.error('Error processing file events:', error);
        } finally {
            this.isProcessing = false;

            // 如果处理期间又有新事件加入，继续处理
            if (this.queue.length > 0) {
                this.processDebounced();
            }
        }
    }

    private optimizeEvents(events: FileEvent[]): FileEvent[] {
        const deleteEvents = events.filter(e => e.type === FileEventType.DELETE);

        if (deleteEvents.length > 1) {
            // 找出所有删除路径的公共父路径
            const paths = deleteEvents.map(e => e.path);
            const commonParentPaths = this.findCommonParentPaths(paths);

            // 创建对优化后的父路径的删除事件
            return commonParentPaths.map(parentPath => ({
                type: FileEventType.DELETE,
                path: parentPath,
                timestamp: Date.now()
            }));
        }

        return events;
    }

    private findCommonParentPaths(paths: string[]): string[] {

        // 找出每个路径的所有父路径
        const allParentPaths = new Set<string>();

        paths.forEach(path => {
            allParentPaths.add(path); 
        })

        // 过滤出最优父路径（不包含彼此的父子关系）
        const result: string[] = [];
        const sortedParents = Array.from(allParentPaths).sort((a, b) => a.length - b.length);
        
        for (const parentPath of sortedParents) {
            // 检查这个路径是否已经被某个较短的路径覆盖
            const isAlreadyCovered = result.some(existingPath =>
                parentPath.startsWith(existingPath + '/') || parentPath === existingPath
            );

            if (!isAlreadyCovered) {
                result.push(parentPath);
            }
        }

        return result;
    }
}

export const fileEventQueue = new FileEventQueue();